/**
 * プランにスポットを足すパネル
 *
 * 常設シートの中身。地図から選ぶ導線は背景の地図が担うので、ここはリストと検索だけを持つ。
 * 選択は地図と共有するため、状態は呼び出し側が持つ。
 */

import { Search } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { getStampRally } from '@/api/services/stamp-rally'
import type { AddPlanSpotParams } from '@/api/types/route-plan'
import type { PlaceSearchResult, StampRallyItem, StampRallySummary } from '@/api/types/stamp-rally'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'

import { SelectAllRow } from '../../components/select-all-row'
import { SpotSearchField } from '../../components/spot-search-field'

type Source = 'rally' | 'other'

type Props = {
  origin: { latitude: number; longitude: number } | null
  /** すでにプランに入っている場所。二重に出さない */
  excludePlaceIds: number[]
  rallies: StampRallySummary[]
  /** 地図と共有する絞り込み対象のラリー */
  rallyId: number | null
  onSelectRally: (rallyId: number | null) => void
  /** 地図のピンと同じ選択を指す */
  selectedPlaceIds: number[]
  onTogglePlace: (placeId: number) => void
  /** 一覧に出ている分をまとめて選ぶ */
  onSelectPlaces: (placeIds: number[]) => void
  onAddSpot: (spot: AddPlanSpotParams) => Promise<boolean>
  isSubmitting: boolean
}

export const PlanAddSpotPanel = ({
  origin,
  excludePlaceIds,
  rallies,
  rallyId,
  onSelectRally,
  selectedPlaceIds,
  onTogglePlace,
  onSelectPlaces,
  onAddSpot,
  isSubmitting,
}: Props) => {
  const [source, setSource] = useState<Source>('rally')
  const [items, setItems] = useState<StampRallyItem[]>([])
  const [keyword, setKeyword] = useState('')
  const [isItemsLoading, setIsItemsLoading] = useState(false)

  useEffect(() => {
    if (rallyId === null) {
      setItems([])
      return
    }
    let aborted = false
    setIsItemsLoading(true)
    getStampRally(rallyId)
      .then((detail) => {
        if (aborted) return
        setItems(detail.groups.flatMap((group) => group.items).filter((item) => !item.stamped_at))
      })
      .catch(() => {
        if (!aborted) toast.error('スポットの取得に失敗しました')
      })
      .finally(() => {
        if (!aborted) setIsItemsLoading(false)
      })
    return () => {
      aborted = true
    }
  }, [rallyId])

  const submitSpot = async (result: PlaceSearchResult) => {
    if (!result.google_place_id) return
    await onAddSpot({
      google_place_id: result.google_place_id,
      name: result.name,
      address: result.address,
      latitude: result.latitude,
      longitude: result.longitude,
    })
  }

  const query = keyword.trim().toLowerCase()
  const visibleItems = items.filter(
    (item) =>
      !excludePlaceIds.includes(item.place_id) &&
      (query === '' ||
        item.name.toLowerCase().includes(query) ||
        (item.address?.toLowerCase().includes(query) ?? false)),
  )
  const unselectedIds = visibleItems.map((item) => item.place_id).filter((id) => !selectedPlaceIds.includes(id))

  return (
    <div className="pb-6">
      <div className="px-3 pb-3">
        <Tabs value={source} onValueChange={(value) => setSource(value as Source)}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="rally">ラリーから</TabsTrigger>
            <TabsTrigger value="other">場所を検索</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div className={cn('space-y-3 px-3', source !== 'rally' && 'hidden')}>
        <div className="flex flex-wrap gap-2">
          {rallies.map((rally) => {
            const active = rallyId === rally.id
            return (
              <Button
                key={rally.id}
                variant={active ? 'default' : 'outline'}
                size="sm"
                className="px-3!"
                onClick={() => {
                  setKeyword('')
                  onSelectRally(active ? null : rally.id)
                }}
              >
                {rally.title}
              </Button>
            )
          })}
        </div>

        {rallyId !== null && (
          <div className="relative">
            <Search size={15} className="absolute top-1/2 left-3 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="このラリー内をスポット名・住所で絞り込む"
              className="pl-9"
            />
          </div>
        )}

        {isItemsLoading ? (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-10 w-full rounded-lg" />
            ))}
          </div>
        ) : visibleItems.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {rallyId === null
              ? 'ラリーを選ぶと未獲得のスポットが並びます'
              : query === ''
                ? '追加できるスポットがありません'
                : '一致するスポットがありません'}
          </p>
        ) : (
          <div className="space-y-2">
            <SelectAllRow
              count={visibleItems.length}
              unselectedCount={unselectedIds.length}
              onSelectAll={() => onSelectPlaces(unselectedIds)}
            />
            {visibleItems.map((item) => (
              <label key={item.id} className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm">
                <Checkbox
                  checked={selectedPlaceIds.includes(item.place_id)}
                  onCheckedChange={() => onTogglePlace(item.place_id)}
                />
                <span className="min-w-0 flex-1 truncate">{item.name}</span>
              </label>
            ))}
          </div>
        )}

        <p className="text-xs text-muted-foreground">
          {rallyId === null
            ? 'ラリーを選ぶと地図もそのラリーだけに絞り込みます'
            : '地図のピンをタップしても選べます。追加すると訪問順は未最適化に戻ります'}
        </p>
      </div>

      <div className={cn('space-y-2 px-3', source !== 'other' && 'hidden')}>
        <Label>場所を検索</Label>
        <SpotSearchField origin={origin} disabled={isSubmitting} onPicked={(result) => void submitSpot(result)} />
        <p className="text-xs text-muted-foreground">スタンプの対象にはならない、立ち寄り先として追加されます</p>
      </div>
    </div>
  )
}
